// DevicesAccordion.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Accordion from './Accordion';  // Your accordion component
import { toast } from 'react-toastify';
import LoadingSpinner from './LoadingSpinner'; // If you have a spinner component

const DevicesAccordion = ({ categoryId }) => {
  const [loading, setLoading] = useState(false);
  const [components, setComponents] = useState([]); // Holds sub_category groups
  const [openIndex, setOpenIndex] = useState(null);

  const { id } = useParams(); // If needed for the estimate ID from the route
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;

  // Toggle accordion for each sub_category index
  const toggleAccordion = useCallback(
    (index) => {
      setOpenIndex(openIndex === index ? null : index);
    },
    [openIndex]
  );

  // Called when user clicks “Delete” button for a device
  const selectedDelete = async (deviceID) => {
    if (!deviceID) return;
    try {
      const response = await axios.post(`${apiUrl}/es_builder_crud.php`, {
        status: 'deleteEsDevice',
        // Adjust your backend param to match how you identify the device
        data: { deviceID: deviceID }, 
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.data.success) {
        toast.success('Device Deleted', { position: 'top-center' });

        // Remove the device from the current list
        setComponents((prevComponents) =>
          prevComponents
            .map((component) => ({
              ...component,
              list: component.list.filter((item) => item.id !== deviceID),
            }))
            .filter((component) => component.list.length > 0)
        );

      } else {
        toast.error(response.data.message || 'Failed to delete device.', {
          position: 'top-center',
        });
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.', {
        position: 'top-center',
      });
    }
  };

  // Fetch all devices grouped by sub_category for this "categoryId"
  useEffect(() => {
    const fetchDevices = async () => {
      setLoading(true);
      try {
        // The backend expects "equipment_device=true" and "es_builder_estimate_id"
        const response = await axios.get(`${apiUrl}/es_builder_crud.php`, {
          params: {
            equipment_device: true,
            es_builder_estimate_id: categoryId,
          },
        });
        // Expecting ["success", [ { cat: "ANNUNCIATOR", list: [...] }, ... ] ]
        if (response.data[0] === 'success') {
          setComponents(response.data[1] || []);
        } else {
          toast.error(response.data[1], {
            position: 'top-center',
          });
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          navigate('/login');
        } else if (error.response && error.response.status === 403) {
          toast.error("Oops!! you don't have access to this area. Speak to the CFO.", {
            position: 'top-center',
          });
        }
      } finally {
        setLoading(false);
      }
    };

    if (categoryId) {
      fetchDevices();
    }
  }, [apiUrl, categoryId, navigate]);

  return (
    <div className="max-w-[1200px] mx-auto">
      <h2 className="text-xl font-bold p-1">Devices & Sub-Categories</h2>
      {loading ? (
        <LoadingSpinner>Loading...</LoadingSpinner>
      ) : (
        <div className="max-w-lg mx-auto mt-10">
          {components.map((component, index) => (
            <Accordion
              key={index}
              title={component.cat} // e.g. "ANNUNCIATOR"
              content={
                <div className="divide-y-2 p-2">
                  {component.list.map((item) => (
                    <div
                      key={item.id}
                      className="grid grid-cols-[70%_15%_15%] gap-2 py-2 items-center"
                    >
                      {/* Device Name & Description */}
                      <div>
                        <span className="font-bold underline">{item.device}</span>
                        <br />
                        <span className="text-xs">{item.description}</span>
                      </div>

                      {/* Price */}
                      <div>
                        <span>${item.price.toFixed(2)}</span>
                      </div>

                      {/* Delete Button */}
                      <div>
                        <button
                          className="bg-red-500 text-white text-md p-2 rounded w-12"
                          onClick={() => selectedDelete(item.id)}
                        >
                          Del
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              }
              isOpen={openIndex === index}
              toggle={() => toggleAccordion(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default DevicesAccordion;
